import { createContext, useContext, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMe, savePreferences } from '../api/client.js';

/**
 * The signed-in user, loaded once from /api/me.
 *
 * There is no token in here and nothing security-sensitive in localStorage.
 * If the cookie is missing or expired the API returns 401 and the fetch
 * wrapper redirects to /auth/signin, so this hook never has to model an
 * "anonymous" state beyond "still loading".
 */
const SessionContext = createContext(null);

const THEME_KEY = 'vz-theme';

function applyTheme(theme) {
  const value = theme === 'dark' ? 'dark' : 'light';
  document.documentElement.setAttribute('data-bs-theme', value);
  // Cached only so index.html can avoid a flash of the wrong theme on the
  // next load. portal.UserPreferences remains the source of truth.
  try { localStorage.setItem(THEME_KEY, value); } catch { /* private mode */ }
}

function currentTheme() {
  return document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
}

export function SessionProvider({ children }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });

  /* ----------------------------------------------------------------------
     Coming back with the back button.

     Browsers keep a whole page alive in the back/forward cache: the JavaScript
     heap, React's state, and every answer this query client has cached. A
     restored page does not remount, and refetchOnWindowFocus is off, so
     nothing revalidates — the portal reappears exactly as it was, drawn from a
     session that may no longer exist. That is the blank shell people see after
     signing out and pressing back: the chrome renders from cache while the
     report iframes hold dead embed tokens.

     It matters more than it looks. On a shared machine the restored page is
     the PREVIOUS user's portal, with their menu and their cached data, after
     somebody else has signed in.

     So a restore empties the cache. Mounted queries then refetch: /api/me
     either succeeds, and the page is genuinely current, or returns 401 and
     Layout shows the landing page. Emptying rather than refetching just the
     session, because every other cached answer belongs to that session too.

     Keyed on event.persisted, which is true only for a real bfcache restore —
     an ordinary load already starts with an empty cache. */
  useEffect(() => {
    const onPageShow = (event) => { if (event.persisted) queryClient.clear(); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [queryClient]);

  const serverTheme = data?.preferences?.theme;

  // Reconcile with the server once /api/me lands. If the user has no saved
  // preference, keep whatever index.html chose from the system setting.
  useEffect(() => {
    if (serverTheme) applyTheme(serverTheme);
  }, [serverTheme]);

  const preferences = useMutation({
    mutationFn: savePreferences,
    // Repaint immediately; the round trip only persists the choice.
    onMutate: (next) => { if (next?.theme) applyTheme(next.theme); },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] })
  });

  const mutate = preferences.mutate;
  const toggleTheme = useCallback(() => {
    const next = currentTheme() === 'dark' ? 'light' : 'dark';
    mutate({ ...(data?.preferences ?? {}), theme: next });
  }, [data, mutate]);

  /* Menu collapse reuses the existing `menuSize` preference — the same field
     the old portal drove its `data-sidenav-size="condensed"` attribute from,
     so it survives the migration and follows the user between machines. */
  const collapsed = data?.preferences?.menuSize === 'condensed';
  const toggleCollapsed = useCallback(() => {
    const next = collapsed ? 'default' : 'condensed';
    mutate({ ...(data?.preferences ?? {}), menuSize: next });
  }, [collapsed, data, mutate]);

  const value = {
    user: data?.user ?? null,
    // Vizora staff. Only controls whether the console entry renders — the
    // server guard is what actually protects it.
    isPlatformAdmin: !!data?.isPlatformAdmin,
    customer: data?.customer ?? null,
    preferences: data?.preferences ?? {},
    theme: serverTheme ?? currentTheme(),
    toggleTheme,
    collapsed,
    toggleCollapsed,
    savePreferences: mutate,
    isLoading,
    error
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
